/**
 * Canvas Yjs mount (Plan v3-01, todo `canvas-as-yjs-room`).
 *
 * Owns the per-item Y.Doc lifecycle on the canvas:
 *
 *   1) When a workspace_item is rendered, it lazily joins its own
 *      Hocuspocus room (one Y.Doc per item, see ADR-v3-05).
 *   2) The workspace-level room (`workspace:<workspaceId>`) is joined
 *      exactly once per canvas mount and carries presence + comment
 *      ephemera (reserved awareness namespaces `presence.*` /
 *      `comment.*`).
 *   3) Sessions are reference-counted and torn down 5s after the
 *      last consumer unmounts — re-mount of the same item within
 *      that window reuses the live socket instead of a reconnect
 *      storm.
 *
 * The kill switch (`COLLAB_V3_KILL_SWITCH`) is enforced server-side
 * by `/api/widgets/collab/get-token`; this module surfaces the
 * resulting 503 as `status === 'unavailable'` so the chrome can
 * show a degraded-mode banner without throwing.
 *
 * Concurrent-mount race (two users opening the same canvas in the
 * same tick): the advisory lock lives server-side in
 * `lib/db/advisory-locks.ts withItemYjsLock` — the client just
 * connects; the pod serialises the first-write.
 */

'use client';

import { HocuspocusProvider } from '@hocuspocus/provider';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Awareness } from 'y-protocols/awareness';
import * as Y from 'yjs';

export type CanvasYjsStatus = 'idle' | 'connecting' | 'connected' | 'unavailable' | 'error';

export interface CanvasYjsRoom {
  scope: 'workspace' | 'item';
  scopeId: string;
  doc: Y.Doc;
  awareness: Awareness;
  status: CanvasYjsStatus;
  /**
   * True once the provider has completed its initial sync with the pod (the
   * server's persisted Y.Doc state has been applied). Consumers that seed an
   * empty doc (the workspace layout bridge) MUST wait for this so they don't
   * double-seed before the authoritative state arrives.
   */
  synced: boolean;
  /** Last error message, if any. Cleared on reconnect. */
  error: string | null;
  /** RFC 7635 TURN creds embedded in the JWT (consumed by multi-party RTC widgets). */
  turn: { url: string; username: string; credential: string; expiresAt: string } | null;
  leave: () => void;
  /**
   * Resolve once every local doc change has been acknowledged by the pod
   * (initial handshake done AND no unsynced updates), bounded by `timeoutMs`.
   * Call this before a HARD navigation that tears down the socket (e.g. the
   * app-node "open fullscreen" → `/w` page) so the just-made edit can't be
   * lost to the unload. Resolves immediately when there's nothing to flush,
   * and resolves on the timeout as a fallback if the pod never acks.
   */
  flush: (timeoutMs?: number) => Promise<void>;
}

interface TokenResponse {
  ok: true;
  token: string;
  wsUrl: string;
  expiresAt: string;
  turn: { url: string; username: string; credential: string; expiresAt: string } | null;
  roomId: string;
}

interface CanvasYjsContextValue {
  workspaceId: string;
  /**
   * Local user identity (mirrors `host.user.current()` payload). Used
   * to seed awareness `user` so presence + comment.* layers see a
   * consistent identity per peer.
   */
  localUser: { userId: string; username: string; color: string; avatarUrl?: string };
  joinItem(itemId: string): CanvasYjsRoom;
  workspaceRoom: CanvasYjsRoom;
}

const CanvasYjsContext = createContext<CanvasYjsContextValue | null>(null);

// ---------------------------------------------------------------------------
// Room manager — keeps one provider per (scope, scopeId) across components.
// ---------------------------------------------------------------------------

interface ManagedRoom {
  refCount: number;
  room: CanvasYjsRoom;
  /** Pending tear-down timer; cleared on re-acquire. */
  teardownTimer: ReturnType<typeof setTimeout> | null;
  provider: HocuspocusProvider | null;
  /** Subscribers awaiting status / error updates so React re-renders. */
  subscribers: Set<() => void>;
  /** Mutated by the provider event handlers; rooms are snapshot-cloned on read. */
  mutable: { status: CanvasYjsStatus; error: string | null; synced: boolean };
}

const TEARDOWN_GRACE_MS = 5_000;

class CanvasYjsManager {
  private readonly rooms = new Map<string, ManagedRoom>();
  // Each room gets its own url-based HocuspocusProvider (it owns its socket),
  // mirroring the working widget collab path. No shared socket to multiplex.

  private keyFor(scope: 'workspace' | 'item', scopeId: string): string {
    return `${scope}:${scopeId}`;
  }

  acquire(
    scope: 'workspace' | 'item',
    scopeId: string,
    localUser: CanvasYjsContextValue['localUser'],
    onChange: () => void,
  ): { room: CanvasYjsRoom; release: () => void } {
    const key = this.keyFor(scope, scopeId);
    let entry = this.rooms.get(key);

    if (!entry) {
      const doc = new Y.Doc();
      const mutable = { status: 'idle' as CanvasYjsStatus, error: null as string | null, synced: false };
      entry = {
        refCount: 0,
        provider: null,
        teardownTimer: null,
        subscribers: new Set(),
        mutable,
        // Awareness gets attached once the provider connects; we expose
        // a placeholder so the caller can subscribe synchronously.
        room: {
          scope,
          scopeId,
          doc,
          // Real Awareness is attached once HocuspocusProvider boots.
          awareness: null as unknown as Awareness,
          status: 'idle',
          synced: false,
          error: null,
          turn: null,
          leave: () => undefined,
          flush: (timeoutMs?: number) => this.flush(key, timeoutMs ?? 2500),
        },
      };
      this.rooms.set(key, entry);
      this.boot(entry, scope, scopeId, localUser).catch((err) => {
        entry!.mutable.status = 'error';
        entry!.mutable.error = err instanceof Error ? err.message : String(err);
        entry!.subscribers.forEach((s) => s());
      });
    }

    entry.refCount += 1;
    entry.subscribers.add(onChange);
    if (entry.teardownTimer) {
      clearTimeout(entry.teardownTimer);
      entry.teardownTimer = null;
    }

    const room = this.snapshot(entry);
    return {
      room,
      release: () => {
        entry!.refCount -= 1;
        entry!.subscribers.delete(onChange);
        if (entry!.refCount === 0) {
          entry!.teardownTimer = setTimeout(() => {
            this.tearDown(key);
          }, TEARDOWN_GRACE_MS);
        }
      },
    };
  }

  private snapshot(entry: ManagedRoom): CanvasYjsRoom {
    return {
      ...entry.room,
      status: entry.mutable.status,
      error: entry.mutable.error,
      synced: entry.mutable.synced,
    };
  }

  /**
   * Live snapshot for a mounted room, or null if not mounted. The acquire-time
   * snapshot goes STALE the moment the provider connects/syncs (status / synced
   * / error are mutated in place), so hooks must re-read this on every render
   * rather than caching the first snapshot — otherwise consumers gated on
   * `synced` (e.g. the layout bridge's seed) never observe it flip true.
   */
  peek(scope: 'workspace' | 'item', scopeId: string): CanvasYjsRoom | null {
    const entry = this.rooms.get(this.keyFor(scope, scopeId));
    return entry ? this.snapshot(entry) : null;
  }

  /**
   * Resolve once the pod has acknowledged every local doc update for this room
   * (`provider.synced && !provider.hasUnsyncedChanges`), or after `timeoutMs`
   * as a fallback. `hasUnsyncedChanges` decrements on the server's SyncStatus
   * ack (see @hocuspocus/provider), so a `false` reading means the edit is
   * durably on the pod — safe to hard-navigate away.
   */
  flush(key: string, timeoutMs: number): Promise<void> {
    const entry = this.rooms.get(key);
    const provider = entry?.provider;
    // No provider (SSR / never booted / already torn down) → nothing to flush.
    if (!provider) return Promise.resolve();
    if (provider.synced && !provider.hasUnsyncedChanges) return Promise.resolve();
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        try {
          provider.off('unsyncedChanges', check);
          provider.off('synced', check);
        } catch {
          /* best-effort listener cleanup */
        }
        clearTimeout(timer);
        resolve();
      };
      const check = () => {
        if (provider.synced && !provider.hasUnsyncedChanges) finish();
      };
      const timer = setTimeout(finish, timeoutMs);
      provider.on('unsyncedChanges', check);
      provider.on('synced', check);
      check();
    });
  }

  private tearDown(key: string): void {
    const entry = this.rooms.get(key);
    if (!entry || entry.refCount > 0) return;
    try {
      entry.provider?.destroy();
    } catch (err) {
      console.warn('[canvas-yjs] provider.destroy failed:', err);
    }
    try {
      entry.room.doc.destroy();
    } catch (err) {
      console.warn('[canvas-yjs] doc.destroy failed:', err);
    }
    this.rooms.delete(key);
  }

  private async boot(
    entry: ManagedRoom,
    scope: 'workspace' | 'item',
    scopeId: string,
    localUser: CanvasYjsContextValue['localUser'],
  ): Promise<void> {
    entry.mutable.status = 'connecting';
    entry.subscribers.forEach((s) => s());

    let token: TokenResponse;
    try {
      const res = await fetch('/api/widgets/collab/get-token', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ scope, scopeId }),
      });
      const body = (await res.json()) as TokenResponse | { ok: false; error: { code: string; message: string } };
      if (!res.ok || !('ok' in body) || !body.ok) {
        const code = res.status === 503 ? 'unavailable' : 'error';
        entry.mutable.status = code as CanvasYjsStatus;
        entry.mutable.error =
          'error' in body && body.error?.message ? body.error.message : `token mint failed: ${res.status}`;
        entry.subscribers.forEach((s) => s());
        return;
      }
      token = body;
    } catch (err) {
      entry.mutable.status = 'error';
      entry.mutable.error = err instanceof Error ? err.message : String(err);
      entry.subscribers.forEach((s) => s());
      return;
    }

    entry.room.turn = token.turn;

    // Collab JWTs are SHORT-LIVED (5 min) and SINGLE-USE (jti replay guard) —
    // a provider that replays its boot-time token is rejected (`token_replay`),
    // so a static token strands the canvas doc PERMANENTLY on the first LB
    // blip / collab pod restart / scale-in. Hocuspocus invokes a function
    // `token` on every connection attempt: the boot token rides exactly once,
    // every later attempt mints FRESH (in-flight dedup so concurrent auth
    // rounds share one mint; mint rate is naturally bounded by the provider's
    // reconnect backoff). On a mint failure we return the LAST token rather
    // than throw — a throw inside the provider's sendToken() stalls the
    // attempt until the pod's 30s idle timeout, while a replay-rejected
    // token fails fast into the next backoff attempt. Same scheme as
    // public/widgets/elaisium/net/provider.js.
    const state = { boot: token.token as string | null, last: token.token, inflight: null as Promise<string> | null };
    const freshToken = async (): Promise<string> => {
      if (state.boot) {
        const t = state.boot;
        state.boot = null;
        return t;
      }
      if (!state.inflight) {
        state.inflight = (async () => {
          const res = await fetch('/api/widgets/collab/get-token', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ scope, scopeId }),
          });
          const body = (await res.json()) as TokenResponse | { ok: false };
          if (res.ok && body.ok && body.token) {
            state.last = body.token;
            if (body.turn) entry.room.turn = body.turn;
          }
          return state.last;
        })()
          .catch(() => state.last)
          .finally(() => {
            state.inflight = null;
          });
      }
      return state.inflight;
    };

    // Dedicated url-based provider per room — the SAME pattern the working
    // widget collab uses (public/widgets/shared/collab-boot.js: `new
    // HocuspocusProvider({ url, name, token, document })`).
    //
    // We previously multiplexed every room over ONE shared
    // HocuspocusProviderWebsocket to save sockets. That silently never opened
    // the documents (workspace + item docs never synced to the pod — no
    // onLoadDocument, no persistence — for ~9 days), while host.documents /
    // host.content over their own url-based providers kept working. Matching
    // the widget path fixes it: HocuspocusProvider owns its socket lifecycle.
    const provider = new HocuspocusProvider({
      url: token.wsUrl,
      name: token.roomId,
      document: entry.room.doc,
      token: freshToken,
      onAuthenticated: () => {
        entry.mutable.status = 'connected';
        entry.mutable.error = null;
        entry.subscribers.forEach((s) => s());
      },
      onSynced: () => {
        // Initial document sync complete — the pod's persisted state has been
        // applied. The layout bridge gates its empty-doc seed on this so it
        // can't double-seed before the authoritative state arrives.
        entry.mutable.synced = true;
        entry.subscribers.forEach((s) => s());
      },
      onAuthenticationFailed: (payload: { reason?: string }) => {
        const reason = payload.reason ?? 'authentication_failed';
        // Replay / expiry / redis-blip rejections are TRANSIENT under the
        // mint-per-attempt scheme — the retry loop's next attempt carries a
        // fresh token, so keep the UI on 'connecting'. Everything else
        // (wrong scope, bad signature) is terminal.
        if (/^token_(replay|expired|internal)$/.test(reason)) {
          entry.mutable.status = 'connecting';
          entry.mutable.error = null;
        } else {
          entry.mutable.status = 'unavailable';
          entry.mutable.error = reason;
        }
        entry.subscribers.forEach((s) => s());
      },
      onDisconnect: () => {
        if (entry.mutable.status === 'connected') {
          entry.mutable.status = 'connecting';
          entry.subscribers.forEach((s) => s());
        }
      },
      onClose: (payload: { event?: { code: number; reason?: string } }) => {
        const event = payload.event;
        if (event && event.code >= 4000) {
          entry.mutable.status = 'unavailable';
          entry.mutable.error = event.reason || `socket closed (${event.code})`;
          entry.subscribers.forEach((s) => s());
        }
      },
    });

    entry.provider = provider;
    // HocuspocusProvider attaches a y-protocols Awareness once the document
    // is wired; expose it via the room snapshot. Seed the local user record
    // immediately so other peers see us as soon as they sync.
    entry.room.awareness = provider.awareness as Awareness;
    provider.awareness?.setLocalStateField('user', {
      userId: localUser.userId,
      username: localUser.username,
      color: localUser.color,
      avatarUrl: localUser.avatarUrl,
      peerId: provider.awareness.clientID.toString(36),
    });

    entry.subscribers.forEach((s) => s());
  }
}

// One manager per browser tab — module-level so it survives React strict-mode
// double-mounts in dev.
const manager = typeof window === 'undefined' ? null : new CanvasYjsManager();

// ---------------------------------------------------------------------------
// React surface
// ---------------------------------------------------------------------------

interface CanvasYjsProviderProps {
  workspaceId: string;
  localUser: CanvasYjsContextValue['localUser'];
  children: React.ReactNode;
}

/**
 * Mounts the workspace-scoped Y.Doc (carries presence + comment
 * awareness) and exposes per-item joins to descendants.
 */
export function CanvasYjsProvider({ workspaceId, localUser, children }: CanvasYjsProviderProps) {
  const workspaceRoom = useYjsRoom('workspace', workspaceId, localUser);

  // Per-item rooms are created on first join and cached for the
  // provider's lifetime so that opening 200 items doesn't churn the
  // socket. Cache is keyed by itemId; entries auto-release when
  // their last consumer unmounts (via the manager's grace period).
  const itemRoomsRef = useRef(new Map<string, CanvasYjsRoom>());

  const joinItem = useCallback(
    (itemId: string): CanvasYjsRoom => {
      const cached = itemRoomsRef.current.get(itemId);
      if (cached) return cached;
      // We deliberately do NOT useYjsRoom here because the caller is a
      // function, not a component. The room is acquired with refCount 1
      // and never released until the workspace provider unmounts.
      if (!manager) {
        throw new Error('CanvasYjsProvider must mount in a browser environment');
      }
      const handle = manager.acquire('item', itemId, localUser, () => {
        // The cached room is replaced with a fresh snapshot on each
        // status change so the next read sees it. We don't trigger a
        // re-render here — components that care subscribe via
        // `useItemYjsRoom`.
        const fresh = manager.acquire('item', itemId, localUser, () => undefined);
        itemRoomsRef.current.set(itemId, fresh.room);
        fresh.release();
      });
      itemRoomsRef.current.set(itemId, handle.room);
      return handle.room;
    },
    [localUser],
  );

  // Release ALL item rooms when the workspace provider unmounts so the
  // manager can collect them.
  useEffect(() => {
    const map = itemRoomsRef.current;
    return () => {
      map.forEach((room) => {
        try {
          room.leave();
        } catch {
          // best-effort
        }
      });
      map.clear();
    };
  }, []);

  const value = useMemo<CanvasYjsContextValue>(
    () => ({ workspaceId, localUser, joinItem, workspaceRoom }),
    [workspaceId, localUser, joinItem, workspaceRoom],
  );

  return <CanvasYjsContext.Provider value={value}>{children}</CanvasYjsContext.Provider>;
}

export function useCanvasYjs(): CanvasYjsContextValue {
  const value = useContext(CanvasYjsContext);
  if (!value) {
    throw new Error('useCanvasYjs must be called inside <CanvasYjsProvider>');
  }
  return value;
}

/**
 * Non-throwing variant — returns `null` when there's no
 * `<CanvasYjsProvider>` above (e.g. the anon read-only canvas, which
 * has no collab token). Presence / collab chrome uses this to no-op
 * gracefully instead of crashing the tree.
 */
export function useOptionalCanvasYjs(): CanvasYjsContextValue | null {
  return useContext(CanvasYjsContext);
}

/** Internal hook that subscribes to one (scope, scopeId) room. */
function useYjsRoom(
  scope: 'workspace' | 'item',
  scopeId: string,
  localUser: CanvasYjsContextValue['localUser'],
): CanvasYjsRoom {
  const [, forceRender] = useState(0);
  const tick = useCallback(() => forceRender((n) => n + 1), []);
  const handleRef = useRef<{ room: CanvasYjsRoom; release: () => void } | null>(null);

  if (!handleRef.current) {
    if (!manager) {
      // SSR fallback — return a stub room. Real wiring happens on hydration.
      const doc = new Y.Doc();
      return {
        scope,
        scopeId,
        doc,
        awareness: null as unknown as Awareness,
        status: 'idle',
        synced: false,
        error: null,
        turn: null,
        leave: () => undefined,
        flush: () => Promise.resolve(),
      };
    }
    handleRef.current = manager.acquire(scope, scopeId, localUser, tick);
  }

  useEffect(() => {
    return () => {
      handleRef.current?.release();
      handleRef.current = null;
    };
    // Lifetime is tied to (scope, scopeId) — re-mounts with a different
    // key get a different effect run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scopeId]);

  // Re-read the LIVE snapshot each render (the `tick` subscription above forces
  // a re-render on every provider event), so consumers see status / synced /
  // error update — the cached acquire-time room is stale after connect.
  return (manager ? manager.peek(scope, scopeId) : null) ?? handleRef.current.room;
}

/** Public hook for components that render a single item. */
export function useItemYjsRoom(itemId: string): CanvasYjsRoom {
  const { localUser } = useCanvasYjs();
  return useYjsRoom('item', itemId, localUser);
}
